
import { useCallback, useRef } from 'react';
import axios from 'axios';
import { extractUserFromToken } from '@/utils/tokenUtils';
import { User } from '@/utils/types';
import { storeTokens } from '@/utils/readToken';
import { applyWebsiteScopeHeaders } from '@/lib/websiteScope';
import { PUBLIC_API_BASE_URL } from '@/utils/publicApiConfig';

const API_BASE_URL = PUBLIC_API_BASE_URL;
const ACCESS_TOKEN_NAME = 'admin_access_token';
const REFRESH_TOKEN_NAME = 'admin_refresh_token';
const SILENT_REFRESH_BUFFER = 60_000; // 1 minute

interface UseTokenRefreshProps {
  api: any;
  setUser: (user: User | null) => void;
  setIsAuthenticated: (value: boolean) => void;
  logout: () => void;
}

export const useTokenRefresh = ({
  api,
  setUser,
  setIsAuthenticated,
  logout,
}: UseTokenRefreshProps) => {
  const refreshInFlight = useRef<Promise<string> | null>(null);
  const refreshTimer = useRef<number | null>(null);

  const scheduleSilentRefresh = useCallback(
    (accessToken: string) => {
      if (refreshTimer.current) {
        window.clearTimeout(refreshTimer.current);
      }

      let delay = 0;
      try {
        const decoded = extractUserFromToken(accessToken);
        if (decoded && decoded.exp) {
          const expiryMs = (decoded.exp as number) * 1000 - Date.now();
          delay = Math.max(0, expiryMs - SILENT_REFRESH_BUFFER);
        }
      } catch {
        delay = 0;
      }

      refreshTimer.current = window.setTimeout(() => {
        const refresh = localStorage.getItem(REFRESH_TOKEN_NAME);
        if (refresh) {
          refreshAccessToken(refresh).catch(() => {
            // If refresh fails we will log the user out inside refreshAccessToken
          });
        }
      }, delay);
    },
    [api]
  );

  const refreshAccessToken = useCallback(
    async (refreshToken: string): Promise<string> => {
      if (refreshInFlight.current) {
        return refreshInFlight.current;
      }

      refreshInFlight.current = (async () => {
        try {
          const response = await axios.post(
            `${API_BASE_URL}/token/refresh/`,
            { refresh: refreshToken },
            { headers: applyWebsiteScopeHeaders({ 'Content-Type': 'application/json' }) }
          );
          const { access } = response.data;

          // Store the new access token
          localStorage.setItem(ACCESS_TOKEN_NAME, access);
          api.defaults.headers.common['Authorization'] = `Bearer ${access}`;

          setIsAuthenticated(true);
          const newUser = extractUserFromToken(access);
          setUser(newUser);

          scheduleSilentRefresh(access);

          return access;
        } catch (err) {
          logout();
          throw err;
        } finally {
          refreshInFlight.current = null;
        }
      })();

      return refreshInFlight.current;
    },
    [api, scheduleSilentRefresh, setIsAuthenticated, setUser, logout]
  );

  const clearRefreshTimer = () => {
    if (refreshTimer.current) {
      window.clearTimeout(refreshTimer.current);
    }
  };

  return {
    refreshAccessToken,
    scheduleSilentRefresh,
    clearRefreshTimer,
  };
};
